import { CheckSquare, Square, AlertTriangle } from "lucide-react";

export const DomainWarmupChecklist = () => {
  const preWarmupChecklist = [
    { task: "SPF record configured and validated", critical: true },
    { task: "DKIM signing enabled and tested", critical: true },
    { task: "DMARC policy set (start with p=none)", critical: true },
    { task: "Google Postmaster Tools configured", critical: true },
    { task: "Microsoft SNDS registered (if targeting Outlook)", critical: false },
    { task: "Clean email list (remove invalids, bounces)", critical: true },
    { task: "Unsubscribe link working in all emails", critical: true },
    { task: "Reply handling set up (don't use no-reply@)", critical: false },
    { task: "Bounce handling configured", critical: false },
    { task: "Email templates tested across clients", critical: false }
  ];

  const duringWarmupChecklist = [
    { task: "Daily monitoring of open rates (target 40%+)", critical: true },
    { task: "Daily monitoring of bounce rates (target <2%)", critical: true },
    { task: "Weekly check of spam complaints (target <0.1%)", critical: true },
    { task: "Google Postmaster domain reputation check", critical: true },
    { task: "Blacklist monitoring (use MXToolbox)", critical: false },
    { task: "Gradual volume increases per schedule", critical: true },
    { task: "Consistent daily sending (7 days/week)", critical: false },
    { task: "Engagement-based recipient selection", critical: true }
  ];

  const postWarmupChecklist = [
    { task: "Achieved 95%+ inbox placement rate", critical: true },
    { task: "Domain reputation: High in Google Postmaster", critical: true },
    { task: "Spam rate consistently below 0.1%", critical: true },
    { task: "DMARC policy upgraded to p=quarantine or p=reject", critical: false },
    { task: "Ongoing monitoring schedule established", critical: false },
    { task: "Automated alerts for reputation drops", critical: false }
  ];

  const renderChecklist = (items: { task: string; critical: boolean }[], title: string) => (
    <div className="bg-card border border-border rounded-xl p-5 sm:p-6">
      <h3 className="font-display font-semibold text-foreground mb-4">{title}</h3>
      <div className="space-y-3">
        {items.map((item, index) => (
          <div key={index} className="flex items-start gap-3">
            <Square className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <span className="text-sm text-foreground">{item.task}</span>
              {item.critical && (
                <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-destructive/10 text-destructive rounded">
                  Critical
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <section className="py-8 sm:py-10">
      <div className="mb-6 sm:mb-8">
        <div className="flex items-center gap-2 mb-2">
          <CheckSquare className="w-5 h-5 text-primary" />
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground">
            Warmup Checklists
          </h2>
        </div>
        <p className="text-muted-foreground">
          Step-by-step verification for each phase of your domain warmup
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 sm:gap-5">
        {renderChecklist(preWarmupChecklist, "Before You Start")}
        {renderChecklist(duringWarmupChecklist, "During Warmup")}
        {renderChecklist(postWarmupChecklist, "After Warmup Complete")}
      </div>

      <div className="mt-6 bg-warning/10 border border-warning/20 rounded-xl p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-warning shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-foreground text-sm mb-1">
              If Warmup Stalls or Reputation Drops
            </h4>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>1. Reduce volume by 50% and pause for 48 hours</li>
              <li>2. Check for blacklist entries (MXToolbox)</li>
              <li>3. Review recent bounce and complaint rates</li>
              <li>4. Verify authentication records are still valid</li>
              <li>5. Resume at 50% of previous volume, increase more slowly</li>
            </ul>
            <div className="text-[10px] text-warning/70 mt-2">
              Source: Validity Email Deliverability Recovery Guide 2024
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
